<template>
  <div class="upload-section">
    <div class="upload-heading">
      <div>
        <span class="upload-label">
          Изображения компании
        </span>

        <h3>
          Обложка и логотип
        </h3>

        <p>
          Добавьте изображения, которые будут отображаться
          в профиле компании.
        </p>
      </div>
    </div>

    <div class="company-image-uploader">
      <!-- BACKGROUND -->
      <button
        type="button"
        class="background-upload"
        :class="{
          'has-image': backgroundPreview,
          uploading: isUploadingBackground
        }"
        :disabled="isUploadingBackground"
        @click="selectBackground"
      >
        <img
          v-if="backgroundPreview"
          :src="backgroundPreview"
          class="background-preview"
          alt="Обложка компании"
        />

        <div
          v-else
          class="background-empty"
        >
          <div class="upload-main-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 16.5V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V16.5"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M12 16V4M7.5 8.5L12 4L16.5 8.5"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <strong>
            Загрузить обложку
          </strong>

          <span>
            JPG, PNG или WEBP
          </span>

          <small>
            Рекомендуемый размер 1600×600
          </small>
        </div>

        <div
          v-if="backgroundPreview && !isUploadingBackground"
          class="background-hover"
        >
          <div class="hover-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <span>
            Изменить обложку
          </span>
        </div>

        <div
          v-if="isUploadingBackground"
          class="loader-overlay"
        >
          <div class="upload-loader"></div>

          <span>
            Загружаем обложку...
          </span>
        </div>

        <input
          ref="backgroundInput"
          type="file"
          accept="image/*"
          hidden
          @change="onBackgroundChange"
        />
      </button>

      <!-- LOGO -->
      <button
        type="button"
        class="logo-upload"
        :class="{
          'has-image': logoPreview,
          uploading: isUploadingLogo
        }"
        :disabled="isUploadingLogo"
        @click="selectLogo"
      >
        <img
          v-if="logoPreview"
          :src="logoPreview"
          class="logo-preview"
          alt="Логотип компании"
        />

        <div
          v-else
          class="logo-empty"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 5V19M5 12H19"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          <span>
            Лого
          </span>
        </div>

        <div
          v-if="logoPreview && !isUploadingLogo"
          class="logo-hover"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M4 16.5V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V16.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />

            <path
              d="M12 16V4M7.5 8.5L12 4L16.5 8.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div
          v-if="isUploadingLogo"
          class="logo-loader-overlay"
        >
          <div class="upload-loader small"></div>
        </div>

        <input
          ref="logoInput"
          type="file"
          accept="image/*"
          hidden
          @change="onLogoChange"
        />
      </button>

      <div class="logo-caption">
        <strong>
          Логотип компании
        </strong>

        <span>
          Лучше использовать квадратное изображение
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

import CompanyService
  from "../api/CompanyService";

const props = defineProps({
  companyId: {
    type: String,
    required: true
  }
});

const emit = defineEmits([
  "update:backgroundFile",
  "update:logoFile"
]);

const isUploadingLogo = ref(false);
const isUploadingBackground = ref(false);

const backgroundInput = ref(null);
const logoInput = ref(null);

const backgroundPreview = ref(null);
const logoPreview = ref(null);

function selectBackground() {
  if (isUploadingBackground.value) {
    return;
  }

  backgroundInput.value?.click();
}

function selectLogo() {
  if (isUploadingLogo.value) {
    return;
  }

  logoInput.value?.click();
}

async function onBackgroundChange(event) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }

  const previewUrl =
    URL.createObjectURL(file);

  backgroundPreview.value =
    previewUrl;

  isUploadingBackground.value =
    true;

  try {
    const imageUrl =
      await CompanyService
        .uploadCompanyImage(
          file,
          "background"
        );

    console.log(
      "✅ Фон успешно загружен:",
      imageUrl
    );

    emit(
      "update:backgroundFile",
      imageUrl
    );
  } catch (error) {
    console.error(
      "❌ Ошибка при загрузке фона:",
      error?.message || error
    );
  } finally {
    isUploadingBackground.value =
      false;

    event.target.value = "";
  }
}

async function onLogoChange(event) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }

  const previewUrl =
    URL.createObjectURL(file);

  logoPreview.value =
    previewUrl;

  isUploadingLogo.value =
    true;

  try {
    const imageUrl =
      await CompanyService
        .uploadCompanyImage(
          file,
          "logo"
        );

    console.log(
      "✅ Логотип успешно загружен:",
      imageUrl
    );

    emit(
      "update:logoFile",
      imageUrl
    );
  } catch (error) {
    console.error(
      "❌ Ошибка при загрузке логотипа:",
      error?.message || error
    );
  } finally {
    isUploadingLogo.value =
      false;

    event.target.value = "";
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.upload-section {
  width: 100%;
}

/* =========================================================
   HEADING
========================================================= */

.upload-heading {
  margin-bottom: 14px;
}

.upload-label {
  display: block;

  margin-bottom: 4px;

  color: #969ca7;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.upload-heading h3 {
  margin: 0;

  color: #282c33;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 15px;
  line-height: 1.3;
}

.upload-heading p {
  max-width: 540px;

  margin: 5px 0 0;

  color: #858b95;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.5;
}

/* =========================================================
   UPLOADER
========================================================= */

.company-image-uploader {
  position: relative;

  width: 100%;
  max-width: 760px;

  padding-bottom: 68px;
}

/* =========================================================
   BACKGROUND
========================================================= */

.background-upload {
  position: relative;

  width: 100%;
  height: 260px;

  display: block;

  overflow: hidden;

  padding: 0;

  cursor: pointer;

  background:
    linear-gradient(
      145deg,
      #f5f7fa,
      #eef2f7
    );

  border: 1px dashed #cdd5df;
  border-radius: 20px;

  box-shadow:
    inset 0 1px 2px
    rgba(20, 32, 50, 0.025);

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.background-upload:hover:not(:disabled) {
  border-color:
    rgba(62, 133, 237, 0.55);

  box-shadow:
    0 0 0 4px
    rgba(62, 133, 237, 0.07);

  transform:
    translateY(-1px);
}

.background-upload.has-image {
  border-style: solid;
  border-color: #d9dee6;
}

.background-upload:disabled {
  cursor: wait;
}

/* =========================================================
   BACKGROUND IMAGE
========================================================= */

.background-preview {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transition:
    transform 0.35s ease,
    filter 0.25s ease;
}

.background-upload:hover
.background-preview {
  transform: scale(1.015);

  filter: brightness(0.88);
}

/* =========================================================
   EMPTY BACKGROUND
========================================================= */

.background-empty {
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-main-icon {
  width: 50px;
  height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 12px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3997f5,
      #496fe5,
      #655ee2
    );

  border-radius: 15px;

  box-shadow:
    0 9px 20px
    rgba(57, 118, 225, 0.22);
}

.upload-main-icon svg {
  width: 24px;
  height: 24px;
}

.background-empty strong {
  color: #363b44;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
}

.background-empty span {
  margin-top: 4px;

  color: #878e99;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.background-empty small {
  margin-top: 8px;

  color: #afb4bc;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

/* =========================================================
   BACKGROUND HOVER
========================================================= */

.background-hover {
  position: absolute;
  inset: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 8px;

  color: #ffffff;

  background:
    rgba(15, 21, 33, 0.18);

  opacity: 0;

  transition:
    opacity 0.22s ease;
}

.background-upload:hover
.background-hover {
  opacity: 1;
}

.hover-icon {
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(255, 255, 255, 0.16);

  border: 1px solid
    rgba(255, 255, 255, 0.22);

  border-radius: 13px;

  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.hover-icon svg {
  width: 20px;
  height: 20px;
}

.background-hover span {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
}

/* =========================================================
   LOGO
========================================================= */

.logo-upload {
  position: absolute;
  left: 28px;
  bottom: 8px;

  width: 112px;
  height: 112px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  padding: 0;

  cursor: pointer;

  background: #ffffff;

  border:
    5px solid #ffffff;

  border-radius: 28px;

  box-shadow:
    0 12px 28px
    rgba(20, 35, 60, 0.17);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.logo-upload:hover:not(:disabled) {
  transform:
    translateY(-2px);

  box-shadow:
    0 16px 34px
    rgba(20, 35, 60, 0.22);
}

.logo-upload:disabled {
  cursor: wait;
}

.logo-preview {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  border-radius: 23px;

  transition:
    filter 0.2s ease,
    transform 0.2s ease;
}

.logo-upload:hover
.logo-preview {
  filter: brightness(0.75);
  transform: scale(1.04);
}

/* =========================================================
   EMPTY LOGO
========================================================= */

.logo-empty {
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 5px;

  color: #4d83e8;

  background:
    linear-gradient(
      145deg,
      #f5f8fd,
      #edf3fc
    );

  border:
    1px dashed
    rgba(66, 130, 225, 0.35);

  border-radius: 23px;
}

.logo-empty svg {
  width: 24px;
  height: 24px;
}

.logo-empty span {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
}

/* =========================================================
   LOGO HOVER
========================================================= */

.logo-hover {
  position: absolute;
  inset: 5px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    rgba(20, 27, 40, 0.34);

  border-radius: 23px;

  opacity: 0;

  transition:
    opacity 0.2s ease;
}

.logo-upload:hover
.logo-hover {
  opacity: 1;
}

.logo-hover svg {
  width: 23px;
  height: 23px;
}

/* =========================================================
   LOGO CAPTION
========================================================= */

.logo-caption {
  position: absolute;
  left: 158px;
  bottom: 25px;

  display: flex;
  flex-direction: column;

  gap: 2px;
}

.logo-caption strong {
  color: #3e434b;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
}

.logo-caption span {
  color: #9298a2;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

/* =========================================================
   LOADERS
========================================================= */

.loader-overlay,
.logo-loader-overlay {
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 20;

  background:
    rgba(250, 251, 253, 0.82);

  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
}

.loader-overlay {
  flex-direction: column;
  gap: 10px;

  border-radius: 20px;
}

.logo-loader-overlay {
  inset: 5px;

  border-radius: 23px;
}

.loader-overlay span {
  color: #657080;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
}

.upload-loader {
  width: 28px;
  height: 28px;

  border:
    3px solid
    rgba(55, 130, 238, 0.16);

  border-top-color: #3989ef;

  border-radius: 50%;

  animation:
    spin
    0.75s
    linear
    infinite;
}

.upload-loader.small {
  width: 24px;
  height: 24px;
}

/* =========================================================
   DARK THEME
========================================================= */

html[data-theme="dark"]
.upload-label {
  color: #7f858e;
}

html[data-theme="dark"]
.upload-heading h3 {
  color: #f0f1f3;
}

html[data-theme="dark"]
.upload-heading p {
  color: #959ba5;
}

html[data-theme="dark"]
.background-upload {
  background:
    linear-gradient(
      145deg,
      #292b30,
      #24262b
    );

  border-color: #42454c;
}

html[data-theme="dark"]
.background-upload:hover:not(:disabled) {
  border-color:
    rgba(88, 150, 244, 0.65);

  box-shadow:
    0 0 0 4px
    rgba(70, 132, 229, 0.1);
}

html[data-theme="dark"]
.background-upload.has-image {
  border-color: #3b3e44;
}

html[data-theme="dark"]
.background-empty strong {
  color: #f0f1f3;
}

html[data-theme="dark"]
.background-empty span {
  color: #989ea8;
}

html[data-theme="dark"]
.background-empty small {
  color: #6f747d;
}

html[data-theme="dark"]
.logo-upload {
  background: #24262b;

  border-color: #24262b;

  box-shadow:
    0 14px 30px
    rgba(0, 0, 0, 0.34);
}

html[data-theme="dark"]
.logo-empty {
  color: #74aaff;

  background:
    linear-gradient(
      145deg,
      #303239,
      #292b31
    );

  border-color:
    rgba(93, 155, 248, 0.4);
}

html[data-theme="dark"]
.logo-caption strong {
  color: #e7e8eb;
}

html[data-theme="dark"]
.logo-caption span {
  color: #858b95;
}

html[data-theme="dark"]
.loader-overlay,
html[data-theme="dark"]
.logo-loader-overlay {
  background:
    rgba(31, 33, 38, 0.82);
}

html[data-theme="dark"]
.loader-overlay span {
  color: #b1b6bf;
}

/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 640px) {
  .company-image-uploader {
    padding-bottom: 78px;
  }

  .background-upload {
    height: 200px;

    border-radius: 18px;
  }

  .background-empty strong {
    font-size: 12px;
  }

  .logo-upload {
    left: 18px;
    bottom: 15px;

    width: 92px;
    height: 92px;

    border-width: 4px;
    border-radius: 24px;
  }

  .logo-preview,
  .logo-empty,
  .logo-hover,
  .logo-loader-overlay {
    border-radius: 20px;
  }

  .logo-caption {
    left: 124px;
    bottom: 29px;

    max-width:
      calc(100% - 130px);
  }
}

/* =========================================================
   ANIMATION
========================================================= */

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .background-upload,
  .background-preview,
  .background-hover,
  .logo-upload,
  .logo-preview,
  .logo-hover {
    transition: none;
  }
}
</style>